/**
 * 投放时间组合编辑器（华为商店红线规则 · field=投放时间 专用）。
 *
 * 数据契约：ope_value =
 *   {"startHMs":"09:00","endHMs":"24:00","exemptTaskIds":["908792043","990822423"]}
 * （三键：投放时段起止 + 豁免任务ID 列表；豁免 ID 为命中投放时段红线时跳过告警的任务）。
 *
 * 组合实现（merge 语义，核心）：HalfHourBlocksPicker 的序列化契约只产出
 * startHMs/endHMs 两键（本组件不改其契约），故两条交互路径分别 merge 后全量 emit：
 *   - 拖拽色块：取 picker 新产出的 startHMs/endHMs + 当前 value 的既有 exemptTaskIds；
 *   - 增删豁免 ID：取当前 value 的 startHMs/endHMs（原样保留、不重排不格式化）+ 新 ids。
 * emit 为唯一序列化出口，键序固定 startHMs → endHMs → exemptTaskIds；豁免列表清空时
 * 恒输出 "exemptTaskIds":[] 而非省略键——与存量修复 SQL 的幂等守卫
 * （NOT JSON_CONTAINS_PATH）互锁：若清空时省略键，DBA 重跑修复脚本会「复活」默认
 * ID，静默回滚用户决策。
 *
 * 维护警示：未来 ope_value 增第四键时，须同步本组件 parse/emit 的键集合，
 * 否则任何一次交互都会静默丢弃该键。
 */

import { useMemo } from "react";
import { HalfHourBlocksPicker } from "./HalfHourBlocksPicker";
import { IdChips } from "../redlineConfig/IdChips";

/** 三键解析结果（startHMs/endHMs 为字符串则原样保留 30 分钟对齐值，不格式化）。 */
interface LaunchTimeValue {
  startHMs: string;
  endHMs: string;
  exemptTaskIds: string[];
}

/** 编辑器 props：value 为 ope_value JSON 串，onChange 回传序列化后的新串。 */
interface LaunchTimeRangeEditorProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** ope_value JSON → 三键；非法 JSON/非对象降级为未选择空值（不报错）。 */
function parseValue(value: string): LaunchTimeValue {
  try {
    const obj: unknown = JSON.parse(value);
    if (obj !== null && typeof obj === "object") {
      const rec = obj as Record<string, unknown>;
      // exemptTaskIds 仅当为数组且逐元素为 string 才采纳（元素级过滤，防脏数据）
      const ids = Array.isArray(rec.exemptTaskIds)
        ? rec.exemptTaskIds.filter((x): x is string => typeof x === "string")
        : [];
      return {
        startHMs: typeof rec.startHMs === "string" ? rec.startHMs : "00:00",
        endHMs: typeof rec.endHMs === "string" ? rec.endHMs : "00:00",
        exemptTaskIds: ids,
      };
    }
  } catch {
    // 非法 JSON：降级为未选择 + 空豁免列表，由用户重新录入
  }
  return { startHMs: "00:00", endHMs: "00:00", exemptTaskIds: [] };
}

export function LaunchTimeRangeEditor({
  value,
  onChange,
  disabled = false,
}: LaunchTimeRangeEditorProps) {
  const parsed = useMemo(() => parseValue(value), [value]);

  /** 色块拖拽：picker 新值只含两键，须 merge 既有 exemptTaskIds 再全量 emit。 */
  function onPickerChange(pickerNext: string) {
    const next = parseValue(pickerNext);
    onChange(
      JSON.stringify({
        startHMs: next.startHMs,
        endHMs: next.endHMs,
        exemptTaskIds: parsed.exemptTaskIds,
      }),
    );
  }

  /** 增删豁免 ID：时段两键原样保留（不重排不格式化），merge 新 ids 全量 emit。 */
  function onIdsChange(ids: string[]) {
    onChange(
      JSON.stringify({
        startHMs: parsed.startHMs,
        endHMs: parsed.endHMs,
        exemptTaskIds: ids,
      }),
    );
  }

  return (
    <div className="w-full">
      <HalfHourBlocksPicker value={value} onChange={onPickerChange} disabled={disabled} />
      {/* 豁免任务ID：label 传入 IdChips 走两行布局——label 与 chips 同行对齐，
          输入框 + 「添加」在 label 正下方一行（避免 chips 多时输入框挤到行尾） */}
      <div className="mt-2.5">
        <IdChips
          ids={parsed.exemptTaskIds}
          onChange={onIdsChange}
          disabled={disabled}
          label="豁免任务ID"
          placeholder="输入任务 ID，回车添加"
        />
      </div>
      <div className="mt-1 text-[11px] text-text-muted-weak">
        命中投放时段红线时跳过告警的任务 ID
      </div>
    </div>
  );
}
